import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FC, FormEvent } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import Button from '@/components/Button';
import { FormLayout } from '@/components/Forms';
import FormField from '@/components/Forms/FormField';
import { Modal } from '@/components/Modal';
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from '@/components/SidePanel';
import useAlerts from '@/hooks/useAlerts';
import useOnCloseWithConfirmation from '@/hooks/useOnCloseWithConfirmation';
import {
  SubtitlesGenerationProject,
  SubtitlesGenerationPrompt,
} from '@/types/resources';
import { api } from '@/utils/api';
import { productionTeams } from '@/utils/data';
import { handleFormErrors } from '@/utils/helpers';

type FormDataT = {
  video: string;
  thumb: string;
  title: string;
  project_id: string;
  description: string;
  fresh_id: string;
  production_team: string;
  prompt_context: string;
  tone: string;
  frequency: string;
  use_people_also_ask_api: boolean;
  prompt_id: number | null;
};

interface Props {
  onClose: () => void;
  editableProject?: SubtitlesGenerationProject;
}

const Form: FC<Props> = ({ onClose, editableProject }) => {
  const alerts = useAlerts();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data: prompts } = useQuery(['subtitles_generation_prompts'], () =>
    api.get<never, SubtitlesGenerationPrompt[]>(
      '/v1/distribution/subtitles-generation-prompts',
    ),
  );

  const {
    handleSubmit,
    setError,
    control,
    clearErrors,
    formState: { isSubmitting, isDirty },
  } = useForm<FormDataT>({
    defaultValues: {
      video: '',
      thumb: '',
      title: editableProject?.title ?? '',
      project_id: editableProject?.projectId ?? '',
      fresh_id: editableProject?.freshId ?? '',
      production_team: editableProject?.productionTeam ?? '',
      prompt_context: editableProject?.promptContext ?? '',
      prompt_id: null,
      tone: editableProject?.tone ?? 'conversational, playful, and friendly',
      frequency: editableProject?.frequency?.toString() ?? '5',
      use_people_also_ask_api: editableProject?.usePeopleAlsoAskApi ?? false,
    },
  });

  const onCancel = useOnCloseWithConfirmation(onClose, isDirty);

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    handleSubmit(async (data) => {
      try {
        const { id } = editableProject
          ? await api.put<typeof data, { id: number }>(
              `/v1/distribution/subtitles-generation-projects/${editableProject.id}`,
              data,
            )
          : await api.post<typeof data, { id: number }>(
              '/v1/distribution/subtitles-generation-projects',
              data,
            );

        await queryClient.invalidateQueries(['subtitles_generation_projects'], {
          exact: false,
        });

        if (editableProject) {
          onClose();
          return;
        }

        navigate(id.toString());
      } catch (err) {
        handleFormErrors<FormDataT>(err, setError, alerts, true, true);
      }
    })(e);
  };

  return (
    <Modal>
      <SidePanel
        heading={editableProject ? 'Edit Project' : 'New Project'}
        onClose={onCancel}
      >
        <SidePanelBody onSubmit={onSubmit} autoComplete="off">
          <SidePanelContent>
            <FormLayout>
              <FormField<FormDataT>
                name="video"
                type="text"
                inputType="url"
                label="Video Dropbox Link"
                control={control}
                placeholder="Add Link"
              />
              <FormField<FormDataT>
                control={control}
                label="Frequency"
                subLabel="(in seconds)"
                placeholder="Add frequency"
                name="frequency"
              />
              <FormField<FormDataT>
                control={control}
                label="Project ID"
                placeholder="Add ID"
                name="project_id"
              />
              {/* <FormField<FormDataT>
                id="video-input"
                control={control}
                existingMedia={editableProject?.video}
                label="Video"
                type="video"
                name="video"
                notes={
                  <>
                    Aspect ratio: 16:9
                    <br />
                    Max file size: 128GB or 12 hours
                  </>
                }
              /> */}
              <FormField<FormDataT>
                id="thumb-input"
                subLabel="(optional)"
                control={control}
                existingMedia={editableProject?.thumbnail}
                label="Thumb"
                type="image"
                name="thumb"
                notes={
                  <>
                    Formats: JPG, PNG, GIF
                    <br />
                    Size: 1280 x 720 / Up to 10MB
                  </>
                }
              />
              <FormField<FormDataT>
                control={control}
                label="Title"
                subLabel="(optional)"
                name="title"
              />
              <FormField<FormDataT>
                control={control}
                label="Fresh ID"
                subLabel="(optional)"
                placeholder="Add ID"
                name="fresh_id"
              />
              <FormField<FormDataT, string>
                control={control}
                subLabel="(optional)"
                options={productionTeams.map((t) => ({
                  label: t,
                  value: t,
                }))}
                name="production_team"
                label="Production Team"
                type="select"
              />
              <FormField<FormDataT, number>
                control={control}
                subLabel="(optional)"
                options={
                  prompts?.map((t) => ({
                    label: t.title,
                    value: t.id,
                  })) ?? []
                }
                name="prompt_id"
                label="Prompt"
                type="select"
              />
              <FormField<FormDataT>
                control={control}
                type="textarea"
                label="Context"
                placeholder="Add Context"
                name="prompt_context"
              />
              <FormField<FormDataT>
                control={control}
                type="textarea"
                label="Tone"
                placeholder="Add Tone"
                name="tone"
              />
              <FormField<FormDataT>
                control={control}
                type="switch"
                headline='Use "People Also Ask" API'
                name="use_people_also_ask_api"
              />
            </FormLayout>
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button disabled={isSubmitting} loading={isSubmitting}>
              {editableProject ? 'Update' : 'Save'}
            </Button>
            <Button type="button" color="secondary" onClick={onCancel}>
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default Form;
